// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import Loading from "./components/Loading";
import {
  clearAuthState,
  isTokenExpired,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import AuthGuard from "./components/AuthGuard";
import ThemeBootstrapper from "./components/ThemeBootstrapper";
import CssBaseline from "@mui/material/CssBaseline";

// Lazy-loaded pages — add x20-founder pages here as they are built.
const Dashboard = lazy(() => import("./components/Dashboard"));
const JobPipeline = lazy(() => import("./components/JobPipeline"));
const Prospects = lazy(() => import("./components/Prospects"));
const JobApplicationDetail = lazy(() =>
  import("./components/JobApplicationDetail"),
);

// Default site theme — kept in sync with public/site-theme.json so that
// unauthenticated visitors see the SAME brand as authenticated pages from the
// very first frame (no default-palette flash before /site-theme.json loads).
// This mirrors x20-creative, where the unauth views share the owner's theme.
const DEFAULT_SITE_THEME = {
  themeName: "Midnight",
  primaryColor: "#5693c2",
  accentColor: "#7e9aae",
  backgroundColor: "#10202c",
  textColor: "#fcfcfd",
  buttonTextColor: "#fcfcfc",
  secondaryColor: "#5d6a74",
};

// Prevents accidental swipe-back navigation (Safari's white circle arrow gesture)
// eslint-disable-next-line no-unused-vars -- intentionally not rendered (disabled); see note at former usage site
const SwipeNavigationBlocker = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = React.useRef(location.pathname);
  const isBlockingRef = React.useRef(false);

  useEffect(() => {
    currentPathRef.current = location.pathname;
    isBlockingRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const publicPages = ["/login", "/register", "/confirm-signup"];
    const isOnPublicPage = publicPages.some(
      (page) => location.pathname === page,
    );
    if (isOnPublicPage) return;

    const handlePopState = () => {
      if (isBlockingRef.current) return;

      const targetPath = currentPathRef.current;
      const currentUrl = window.location.pathname;

      if (currentUrl !== targetPath) {
        isBlockingRef.current = true;
        window.history.replaceState(null, "", targetPath);
        navigate(targetPath, { replace: true });
        setTimeout(() => {
          isBlockingRef.current = false;
        }, 100);
      }
    };

    window.history.pushState({ preventBack: true }, "", currentPathRef.current);
    window.addEventListener("popstate", handlePopState, { passive: true });

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Lazy initializer — reads THEME from localStorage synchronously on first render.
  // Falls back to DEFAULT_SITE_THEME (not the cyan default) so unauth visitors
  // get the branded look immediately, before /site-theme.json resolves.
  const [theme, setTheme] = useState(() => {
    try {
      // Only honor a saved personal theme for AUTHENTICATED users; unauth visitors
      // get the brand default (refined by the no-store /site-theme.json fetch) so a
      // stale localStorage theme can never flash on first paint.
      const token = localStorage.getItem("jwtToken");
      const isAuthed = token && !isTokenExpired(token);
      if (isAuthed) {
        const savedTheme = localStorage.getItem("THEME");
        if (savedTheme) return JSON.parse(savedTheme);
      }
    } catch (e) {
      console.error("App.js - Error parsing saved theme:", e);
    }
    return DEFAULT_SITE_THEME;
  });

  // Fetch the site owner's selected theme on startup so unauthenticated visitors
  // see the same brand as the authenticated owner. 404s silently if not seeded.
  useEffect(() => {
    fetch("/site-theme.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => {
        if (t && t.primaryColor) handleThemeChange(t);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("App.js - Checking authentication state");

    const cleanup = preventAWSRedirects();

    // Restore session if a valid (non-expired) token already exists.
    const existingToken = localStorage.getItem("jwtToken");
    if (existingToken && !isTokenExpired(existingToken)) {
      console.log("App.js - Valid session found, restoring authenticated state");
      setIsAuthenticated(true);
    } else {
      clearAllAWSState();
      clearAuthState();
      setIsAuthenticated(false);
    }

    return cleanup;
  }, []);

  // Token expiration and inactivity timeout monitoring
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkInterval = setInterval(() => {
      const token = localStorage.getItem("jwtToken");

      if (token && isTokenExpired(token)) {
        console.warn("App.js - Token expired, logging out user");
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
        return;
      }

      const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
      const lastActivityTime = localStorage.getItem("lastActivityTime");

      if (lastActivityTime) {
        const timeSinceLastActivity =
          Date.now() - parseInt(lastActivityTime, 10);

        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          console.warn("App.js - Inactivity timeout reached, logging out user");
          clearAuthState();
          setIsAuthenticated(false);
          window.location.replace("/login");
          return;
        }
      }
    }, 30000); // Check every 30 seconds

    const updateActivityTime = () => {
      if (isAuthenticated) {
        localStorage.setItem("lastActivityTime", Date.now().toString());
      }
    };

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    events.forEach((event) => {
      window.addEventListener(event, updateActivityTime, { passive: true });
    });

    return () => {
      clearInterval(checkInterval);
      events.forEach((event) => {
        window.removeEventListener(event, updateActivityTime);
      });
    };
  }, [isAuthenticated]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <CssBaseline />
          <ThemeBootstrapper
            isAuthenticated={isAuthenticated}
            onThemeLoaded={handleThemeChange}
          />
          {/* SwipeNavigationBlocker disabled: it blocked legitimate browser back-navigation and caused blank-page-on-back via bfcache. */}
          <AuthGuard>
            <Header
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={setIsAuthenticated}
            />
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public routes — accessible without authentication */}
                <Route path="/" element={<Dashboard />} />
                <Route
                  path="/login"
                  element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/register"
                  element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route path="/confirm-signup" element={<ConfirmSignUp />} />

                {/* Outbound marketing hub — authenticated only */}
                <Route
                  path="/prospects"
                  element={
                    isAuthenticated ? (
                      <Prospects />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Job-hunt pipeline pages — authenticated only */}
                <Route
                  path="/jobs"
                  element={
                    isAuthenticated ? (
                      <JobPipeline />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/jobs/applications/:applicationId"
                  element={
                    isAuthenticated ? (
                      <JobApplicationDetail />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />

                {/* Unknown routes fall back to the landing page */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            <Footer />
          </AuthGuard>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
